import React, { useMemo } from "react";
import { VsiBubble, VsiH4, VsiH5 } from "../../../styled/App.styled";
import { useDataWithRoute, useTitleFromComponent } from "../../../utils/hooks";
import { useVsiAppContext } from "../../../stores/VsiApp";
import { Card, Col, Row } from "reactstrap";
import AtAGlance from "../../../components/AtAGlance";
import { dollarFormatter } from "../../../utils/gridFormatters";
import Grid from "../../../components/Grid";
import DonutChart from "../../../components/DonutChart";
import { genericColors } from "../../../utils/chartUtils";
import { cloudSumamryDefs } from "../../../utils/gridColumnDefs/cloud/cloudSummaryDefs";
import dayjs from "dayjs";

const yearAagDefs = [
  {
    field: "spend_this_year",
    mobile: true,
    title: "Spend This Year"
  },
  {
    field: "forecast_spend_this_year",
    mobile: true,
    icon: true,
    diff: true,
    title: "Forecast Spend This Year",
    fieldStart: "spend_last_year",
    type: "forecast-from",
  }
];

const monthAagDefs = [
  {
    field: "spend_this_month",
    mobile: true,
    title: "Spend This Month"
  },
  {
    field: "forecast_spend_this_month",
    mobile: true,
    icon: true,
    diff: true,
    title: "Forecast Spend This Month",
    fieldStart: "spend_last_month",
    type: "forecast-from",
  }
];

export default function CloudSummary({ context, title }) {

  useTitleFromComponent(`${context} / ${title}`);
  const { theme, client, periodId, periodDate } = useVsiAppContext();

  const [data, { isLoading }] = useDataWithRoute(`/api/v6/report/enterprise_cloud_summary_proc?client_id=${client.clientId}&period_id=${periodId}`);
  const [atAGlance, gridData, pieData] = data?.recordsets ?? [[], [], []];

  const dateMonth = useMemo(() => {
    return dayjs(periodDate).format("MMM YYYY");
  }, [periodDate]);

  const atAGlanceData = useMemo(() => {
    const aag = {};
    Object.keys(atAGlance?.[0] ?? {}).forEach(key => {
      if (key.includes("spend")) {
        aag[key] = dollarFormatter({ value: atAGlance?.[0]?.[key]});
      }

      if (key === "forecast_spend_this_year") {
        aag[`${key}_diff`] = dollarFormatter({ value: parseFloat((atAGlance?.[0]?.forecast_spend_this_year - atAGlance?.[0]?.spend_last_year).toFixed(2))});
      }
      if (key === "forecast_spend_this_month") {
        aag[`${key}_diff`] = dollarFormatter({ value: parseFloat((atAGlance?.[0]?.forecast_spend_this_month - atAGlance?.[0]?.spend_last_month).toFixed(2))});
      }
      if (key === "spend_last_month") {
        aag['spend_last_month'] = aag['spend_last_month'] + ` last month` ?? 0;
      }
      if (key === "spend_last_year") {
        aag['spend_last_year'] = aag['spend_last_year'] + ` last year` ?? 0;
      }
    });
    return aag;
  }, [atAGlance]);

  const pieChart = useMemo(() => {
    if (pieData?.length) {
      return pieData.map(datum => (
        {
          name: datum?.device_type_description ?? datum?.device_type_code,
          y: datum.total_cost
        }
      ));
    }
    return [];
  }, [pieData]);

  return <Card className={`report-card p-2 nav-card-${theme}`}>
    <VsiH4>Cloud Summary</VsiH4>
    <VsiBubble noAutobot>
      <Row>
        <Col xs={6} sm={{
          offset: 1,
          size: 3
        }}>
          <AtAGlance config={yearAagDefs}
                     isLoading={isLoading}
                     data={atAGlanceData}
                     size={"xl"}
                     orient={"vertical"} />
        </Col>
        <Col xs={12} sm={4}>
          <DonutChart data={pieChart}
                      alignTitle={"center"}
                      title={`Forecasted Spend by Vendor`}
                      subtitle={{
                        text: `End of ${dateMonth}`
                       }}
                      tooltipFormat={'{series.name}: <b>${point.y}</b><br>'}
                      minHeight={400}
                      colors={genericColors}
                      seriesName={"Spend Forecast"}
                      enableLegend={true} />
        </Col>
        <Col xs={6} sm={3}>
          <AtAGlance config={monthAagDefs}
                     isLoading={isLoading}
                     data={atAGlanceData}
                     size={"xl"}
                     orient={"vertical"} />
        </Col>
      </Row>
    </VsiBubble>
    <VsiBubble noAutobot>
      <VsiH5 className={"mb-2"}>10 Most Expensive Resources</VsiH5>
      <Grid data={gridData}
            columnDefs={cloudSumamryDefs}
            hideRecordCount
            isLoading={isLoading} />
    </VsiBubble>
  </Card>;
}